import React, { useState } from "react";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiX,
  FiMessageCircle,
  FiHash,
  FiPlus,
  FiMinus,
  FiType,
  FiAlignLeft,
  FiTag,
  FiSave,
  FiEye,
  FiCamera,
  FiImage,
} from "react-icons/fi";
import communityService from "../../../services/communityService";
import {
  validateThreadTitle,
  validateMessageContent,
  validateImageUpload,
  sanitizeInput,
  logSecurityEvent,
  rateLimiter,
  handleSecureError,
} from "../../../utils/securityUtils";
import "./CreateThreadModal.css";

// Animation variants
const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: "spring", damping: 25, stiffness: 300 },
  },
  exit: { opacity: 0, scale: 0.95, y: 20, transition: { duration: 0.15 } },
};

const formItemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0 },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

const CreateThreadModal = ({
  communityId,
  community,
  onClose,
  onThreadCreated,
}) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    tags: [],
  });
  const [tagInput, setTagInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPreview, setShowPreview] = useState(false);

  // Photo upload states
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Flair selection
  const [selectedFlair, setSelectedFlair] = useState(null);

  // Common tags suggestions
  const suggestedTags = [
    "research",
    "discussion",
    "question",
    "announcement",
    "collaboration",
    "paper-review",
    "methodology",
    "results",
    "help-needed",
    "resources",
    "conference",
    "journal",
    "tutorial",
    "beginner",
    "advanced",
  ];

  // Predefined flair options
  const predefinedFlairs = [
    { text: "Discussion", color: "#ffffff", backgroundColor: "#3b82f6" },
    { text: "Question", color: "#ffffff", backgroundColor: "#8b5cf6" },
    { text: "Announcement", color: "#ffffff", backgroundColor: "#ef4444" },
    { text: "Research", color: "#ffffff", backgroundColor: "#10b981" },
    { text: "Help Needed", color: "#ffffff", backgroundColor: "#f59e0b" },
    { text: "Tutorial", color: "#ffffff", backgroundColor: "#06b6d4" },
    { text: "News", color: "#ffffff", backgroundColor: "#ec4899" },
    { text: "Meta", color: "#ffffff", backgroundColor: "#64748b" },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleAddTag = (tag) => {
    const trimmedTag = tag.trim().toLowerCase();

    if (!trimmedTag) return;

    if (formData.tags.length >= 5) {
      toast.error("Maximum 5 tags allowed");
      return;
    }

    if (formData.tags.includes(trimmedTag)) {
      toast.error("Tag already added");
      return;
    }

    if (trimmedTag.length > 30) {
      toast.error("Tag must be 30 characters or less");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      tags: [...prev.tags, trimmedTag],
    }));
    setTagInput("");
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      handleAddTag(tagInput);
    }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        validateImageUpload(file);

        setPhotoFile(file);

        const reader = new FileReader();
        reader.onload = (e) => setPhotoPreview(e.target.result);
        reader.readAsDataURL(file);

        logSecurityEvent("thread_photo_selected", {
          communityId,
          fileType: file.type,
          fileSize: file.size,
        });
      } catch (error) {
        toast.error(error.message);

        logSecurityEvent("thread_photo_selection_rejected", {
          communityId,
          reason: error.message,
          fileType: file.type,
          fileSize: file.size,
        });

        e.target.value = "";
      }
    }
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    const fileInput = document.getElementById("ctm-photo-input");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const validateForm = () => {
    const newErrors = {};

    const titleError = validateThreadTitle(formData.title);
    if (titleError) {
      newErrors.title = titleError;
    }

    const descriptionError = validateMessageContent(formData.description);
    if (descriptionError) {
      newErrors.description = descriptionError;
    } else if (formData.description.trim().length < 10) {
      newErrors.description = "Description must be at least 10 characters";
    } else if (formData.description.trim().length > 2000) {
      newErrors.description = "Description must be 2000 characters or less";
    }

    if (Object.keys(newErrors).length > 0) {
      logSecurityEvent("form_validation_failed", {
        form: "create_thread",
        errors: Object.keys(newErrors),
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!rateLimiter.isAllowed("create_thread", 3, 600000)) {
      toast.error(
        "Too many discussion creation attempts. Please wait before trying again."
      );
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setIsUploading(true);

      let photoData = null;

      if (photoFile) {
        try {
          validateImageUpload(photoFile);

          logSecurityEvent("thread_photo_upload_attempt", {
            communityId,
            fileType: photoFile.type,
            fileSize: photoFile.size,
          });

          photoData = await communityService.uploadThreadPhoto(
            communityId,
            photoFile
          );

          logSecurityEvent("thread_photo_uploaded", {
            communityId,
            photoUrl: photoData.photoUrl,
          });
        } catch (photoError) {
          logSecurityEvent("thread_photo_upload_failed", {
            communityId,
            error: photoError.message,
          });

          const securePhotoError = handleSecureError(photoError);
          toast.error(`Failed to upload photo: ${securePhotoError}`);
          return;
        }
      }

      const threadData = {
        title: sanitizeInput(formData.title.trim()),
        description: sanitizeInput(formData.description.trim()),
        tags: formData.tags.map((tag) => sanitizeInput(tag)),
        ...(photoData && {
          photoUrl: photoData.photoUrl,
          photoKey: photoData.photoKey,
        }),
        ...(selectedFlair && { flair: selectedFlair }),
      };
logSecurityEvent("thread_creation_attempt", {
        communityId,
        titleLength: threadData.title.length,
        descriptionLength: threadData.description.length,
        tagsCount: threadData.tags.length,
        hasPhoto: !!photoData,
        hasFlair: !!selectedFlair,
      });

      const response = await communityService.createThread(
        communityId,
        threadData
      );

      if (response.success) {
        const newThread = response.data.thread;
toast.success("Discussion created successfully!");

        logSecurityEvent("thread_created_successfully", {
          communityId,
          threadId: newThread._id,
          flairIncluded: !!selectedFlair,
          singleApiCall: true,
        });

        onThreadCreated(newThread);

        setFormData({
          title: "",
          description: "",
          tags: [],
        });
        setTagInput("");
        setPhotoFile(null);
        setPhotoPreview(null);
        setSelectedFlair(null);
      } else {
        throw new Error("Failed to create discussion");
      }
    } catch (error) {
      const secureErrorMessage = handleSecureError(error);

      logSecurityEvent("thread_creation_failed", {
        communityId,
        error: secureErrorMessage,
        status: error.status,
      });

      if (error.status === 403) {
        toast.error("You must be a community member to create discussions");
      } else if (error.status === 429) {
        toast.error("Too many discussions created. Please wait 10 minutes.");
      } else {
        toast.error(secureErrorMessage);
      }
    } finally {
      setLoading(false);
      setIsUploading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="ctm-backdrop"
        variants={backdropVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={handleBackdropClick}
      >
        <motion.div
          className="ctm-modal"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="ctm-header">
            <div className="ctm-header-title">
              <div className="ctm-header-icon">
                <FiMessageCircle size={20} />
              </div>
              <h2>Start a Discussion</h2>
            </div>

            <div className="ctm-header-actions">
              <button
                type="button"
                className={`ctm-preview-btn ${showPreview ? "active" : ""}`}
                onClick={() => setShowPreview(!showPreview)}
              >
                <FiEye size={16} />
                <span>Preview</span>
              </button>

              <button className="ctm-close-btn" onClick={onClose}>
                <FiX size={20} />
              </button>
            </div>
          </div>

          <div className="ctm-community-banner">
            <span>Posting in</span>
            <strong>{community?.name}</strong>
          </div>

          {/* Content */}
          <div className="ctm-content">
            {!showPreview ? (
              <motion.form
                onSubmit={handleSubmit}
                className="ctm-form"
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
              >
                {/* Title Input */}
                <motion.div className="ctm-form-group" variants={formItemVariants}>
                  <label className="ctm-label">
                    <FiType size={16} />
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="What would you like to discuss?"
                    className={`ctm-input ${errors.title ? "error" : ""}`}
                    maxLength={200}
                  />
                  {errors.title && (
                    <span className="ctm-error-text">{errors.title}</span>
                  )}
                  <div className="ctm-char-count">{formData.title.length}/200</div>
                </motion.div>

                {/* Description Input */}
                <motion.div className="ctm-form-group" variants={formItemVariants}>
                  <label className="ctm-label">
                    <FiAlignLeft size={16} />
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Provide more details about your discussion..."
                    rows={6}
                    className={`ctm-textarea ${errors.description ? "error" : ""}`}
                    maxLength={2000}
                  />
                  {errors.description && (
                    <span className="ctm-error-text">{errors.description}</span>
                  )}
                  <div className="ctm-char-count">
                    {formData.description.length}/2000
                  </div>
                </motion.div>

                {/* Tags Input */}
                <motion.div className="ctm-form-group" variants={formItemVariants}>
                  <label className="ctm-label">
                    <FiTag size={16} />
                    Tags (Optional)
                  </label>

                  {formData.tags.length > 0 && (
                    <div className="ctm-current-tags">
                      {formData.tags.map((tag, index) => (
                        <motion.span
                          key={index}
                          className="ctm-tag-chip"
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                        >
                          <FiHash size={10} />
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="ctm-remove-tag-btn"
                          >
                            <FiMinus size={10} />
                          </button>
                        </motion.span>
                      ))}
                    </div>
                  )}

                  {formData.tags.length < 5 && (
                    <div className="ctm-tag-input-row">
                      <input
                        type="text"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={handleTagInputKeyDown}
                        placeholder="Add tags (press Enter or comma to add)"
                        className="ctm-input"
                        maxLength={30}
                      />
                      <button
                        type="button"
                        onClick={() => handleAddTag(tagInput)}
                        className="ctm-add-tag-btn"
                        disabled={!tagInput.trim()}
                      >
                        <FiPlus size={18} />
                      </button>
                    </div>
                  )}

                  <div className="ctm-suggested-section">
                    <span className="ctm-suggested-label">Suggested:</span>
                    <div className="ctm-suggested-tags">
                      {suggestedTags
                        .filter((tag) => !formData.tags.includes(tag))
                        .slice(0, 8)
                        .map((tag) => (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => handleAddTag(tag)}
                            className="ctm-suggested-tag"
                            disabled={formData.tags.length >= 5}
                          >
                            <FiHash size={10} />
                            {tag}
                          </button>
                        ))}
                    </div>
                  </div>
                </motion.div>

                {/* Flair Selection */}
                <motion.div className="ctm-form-group" variants={formItemVariants}>
                  <label className="ctm-label">
                    <FiTag size={16} />
                    Flair (Optional)
                  </label>
                  <div className="ctm-flair-grid">
                    <button
                      type="button"
                      className={`ctm-flair-option ${!selectedFlair ? "selected" : ""}`}
                      onClick={() => setSelectedFlair(null)}
                    >
                      No Flair
                    </button>
                    {predefinedFlairs.map((flair, index) => (
                      <button
                        key={index}
                        type="button"
                        className={`ctm-flair-option ${
                          selectedFlair?.text === flair.text ? "selected" : ""
                        }`}
                        onClick={() => setSelectedFlair(flair)}
                      >
                        <span
                          className="ctm-flair-badge"
                          style={{
                            color: flair.color,
                            backgroundColor: flair.backgroundColor,
                          }}
                        >
                          {flair.text}
                        </span>
                      </button>
                    ))}
                  </div>
                </motion.div>

                {/* Photo Upload */}
                <motion.div className="ctm-form-group" variants={formItemVariants}>
                  <label className="ctm-label">
                    <FiCamera size={16} />
                    Photo (Optional)
                  </label>

                  <div className="ctm-photo-container">
                    <input
                      id="ctm-photo-input"
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoSelect}
                      disabled={isUploading}
                      style={{ display: "none" }}
                    />

                    {!photoPreview ? (
                      <label
                        htmlFor="ctm-photo-input"
                        className={`ctm-photo-dropzone ${isUploading ? "disabled" : ""}`}
                      >
                        <div className="ctm-photo-dropzone-content">
                          <div className="ctm-photo-dropzone-icon">
                            <FiImage size={24} />
                          </div>
                          <span className="ctm-photo-dropzone-text">
                            Click to add a photo
                          </span>
                          <small className="ctm-photo-dropzone-hint">
                            JPG, PNG, GIF, WebP • Max 5MB
                          </small>
                        </div>
                      </label>
                    ) : (
                      <motion.div
                        className="ctm-photo-preview"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                      >
                        <img src={photoPreview} alt="Thread photo preview" />
                        <button
                          type="button"
                          onClick={handleRemovePhoto}
                          className="ctm-photo-remove-btn"
                          disabled={isUploading}
                          title="Remove photo"
                        >
                          <FiX size={16} />
                        </button>
                      </motion.div>
                    )}
                  </div>
                </motion.div>

                {/* Form Actions */}
                <motion.div className="ctm-actions" variants={formItemVariants}>
                  <button
                    type="button"
                    onClick={onClose}
                    className="ctm-cancel-btn"
                    disabled={loading || isUploading}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="ctm-submit-btn"
                    disabled={loading || isUploading}
                  >
                    {isUploading ? (
                      <>
                        <div className="ctm-spinner"></div>
                        Uploading Photo...
                      </>
                    ) : loading ? (
                      <>
                        <div className="ctm-spinner"></div>
                        Creating...
                      </>
                    ) : (
                      <>
                        <FiSave size={16} />
                        Create Discussion
                      </>
                    )}
                  </button>
                </motion.div>
              </motion.form>
            ) : (
              // Preview Mode
              <motion.div
                className="ctm-preview"
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
              >
                <motion.div className="ctm-preview-header" variants={formItemVariants}>
                  <h3>
                    <FiEye size={16} />
                    Preview
                  </h3>
                </motion.div>

                <motion.div className="ctm-preview-card" variants={formItemVariants}>
                  <h2 className="ctm-preview-title">
                    {formData.title || "Your discussion title will appear here"}
                  </h2>

                  <div className="ctm-preview-description">
                    {formData.description ? (
                      <p>{formData.description}</p>
                    ) : (
                      <p className="placeholder">
                        Your description will appear here
                      </p>
                    )}
                  </div>

                  {photoPreview && (
                    <div className="ctm-preview-photo">
                      <img src={photoPreview} alt="Thread photo preview" />
                    </div>
                  )}

                  {formData.tags.length > 0 && (
                    <div className="ctm-preview-tags">
                      {formData.tags.map((tag, index) => (
                        <span key={index} className="ctm-preview-tag">
                          <FiHash size={10} />
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </motion.div>

                <motion.div className="ctm-preview-actions" variants={formItemVariants}>
                  <button
                    onClick={() => setShowPreview(false)}
                    className="ctm-edit-btn"
                  >
                    <FiType size={16} />
                    Edit
                  </button>

                  <button
                    onClick={handleSubmit}
                    className="ctm-submit-btn"
                    disabled={
                      loading ||
                      isUploading ||
                      !formData.title.trim() ||
                      !formData.description.trim()
                    }
                  >
                    {isUploading ? (
                      <>
                        <div className="ctm-spinner"></div>
                        Uploading Photo...
                      </>
                    ) : loading ? (
                      <>
                        <div className="ctm-spinner"></div>
                        Creating...
                      </>
                    ) : (
                      <>
                        <FiSave size={16} />
                        Create Discussion
                      </>
                    )}
                  </button>
                </motion.div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CreateThreadModal;
